import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { FaCalendarAlt, FaDownload } from 'react-icons/fa';
import { ALL_STATUS_MAPPING, getStatusLabel } from '../constants/ticketStatuses';
import './TicketExportModal.css';

const INCLUDE_OPTIONS = ['messages', 'ai_drafts', 'carrier_messages', 'ai_logs'];
const POLL_INTERVAL_MS = 3000;

const triggerBlobDownload = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const filenameFromDisposition = (disposition) => {
  const match = /filename="?([^";]+)"?/.exec(disposition || '');
  return match ? match[1] : 'tickets_export.json';
};

const TicketExportModal = ({ onClose }) => {
  const { t } = useTranslation();
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [createdFrom, setCreatedFrom] = useState('');
  const [createdTo, setCreatedTo] = useState('');
  const [carrierInput, setCarrierInput] = useState('');
  const [includes, setIncludes] = useState([]);
  const [phase, setPhase] = useState('idle'); // idle | exporting | polling | done | error
  const [job, setJob] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const cancelledRef = useRef(false);

  useEffect(() => () => { cancelledRef.current = true; }, []);

  const toggleValue = (value, list, setList) => {
    setList(list.includes(value) ? list.filter((item) => item !== value) : [...list, value]);
  };

  const buildParams = () => {
    const params = new URLSearchParams();
    selectedStatuses.forEach((status) => params.append('status', status));
    if (createdFrom) params.append('created_from', createdFrom);
    if (createdTo) params.append('created_to', createdTo);
    carrierInput
      .split(',')
      .map((carrier) => carrier.trim())
      .filter(Boolean)
      .forEach((carrier) => params.append('carrier_name', carrier));
    includes.forEach((value) => params.append('include', value));
    return params;
  };

  const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('access_token')}`,
  });

  const downloadJobFile = async (jobId) => {
    const response = await axios.get(`/api/tickets/export/jobs/${jobId}/download`, {
      headers: authHeaders(),
    });
    const link = document.createElement('a');
    link.href = response.data.url;
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const pollJob = async (jobId) => {
    setPhase('polling');
    // Bounded polling loop (same shape as the conversation regeneration poll).
    for (let attempt = 0; attempt < 200; attempt += 1) {
      if (cancelledRef.current) return;
      await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
      if (cancelledRef.current) return;
      try {
        const response = await axios.get(`/api/tickets/export/jobs/${jobId}`, {
          headers: authHeaders(),
        });
        const currentJob = response.data.job;
        setJob(currentJob);
        if (currentJob.status === 'done') {
          setPhase('done');
          await downloadJobFile(jobId);
          return;
        }
        if (currentJob.status === 'failed') {
          setPhase('error');
          setErrorMessage(t('ticketExport.jobFailed', 'The export failed. Please try again.'));
          return;
        }
      } catch (error) {
        // Transient poll errors: keep trying until the loop bound.
      }
    }
    setPhase('error');
    setErrorMessage(t('ticketExport.stillRunning', 'The export is still running — close this window and use the export button again later to retrieve it.'));
  };

  const handleExport = async () => {
    setPhase('exporting');
    setErrorMessage('');
    try {
      const response = await axios.get(`/api/tickets/export?${buildParams().toString()}`, {
        headers: authHeaders(),
        responseType: 'blob',
      });
      if (response.status === 202) {
        const payload = JSON.parse(await response.data.text());
        setJob(payload.job);
        await pollJob(payload.job.id);
      } else {
        triggerBlobDownload(
          response.data,
          filenameFromDisposition(response.headers['content-disposition'])
        );
        setPhase('done');
      }
    } catch (error) {
      setPhase('error');
      setErrorMessage(t('ticketExport.error', 'Export failed. Please try again.'));
    }
  };

  const busy = phase === 'exporting' || phase === 'polling';

  return (
    <div className="tex-modal-overlay" onClick={busy ? undefined : onClose}>
      <div className="tex-modal" onClick={(event) => event.stopPropagation()}>
        <div className="tex-modal-header">
          <h3>{t('ticketExport.title', 'Export tickets')}</h3>
          <button type="button" className="tex-modal-close" onClick={onClose}>✕</button>
        </div>

        <div className="tex-modal-body">
          <div className="tex-field">
            <div className="tex-field-label-row">
              <span className="tex-field-label">{t('ticketExport.statusLabel', 'Statuses')}</span>
              {selectedStatuses.length > 0 && (
                <button type="button" className="tex-clear-btn" onClick={() => setSelectedStatuses([])}>
                  {t('ticketExport.clear', 'Clear')}
                </button>
              )}
            </div>
            <span className="tex-field-hint">
              {selectedStatuses.length === 0
                ? t('ticketExport.allStatuses', 'No selection = all statuses')
                : t('ticketExport.selectedCount', '{{count}} selected', { count: selectedStatuses.length })}
            </span>
            <div className="tex-checkbox-grid">
              {ALL_STATUS_MAPPING.map(({ value }) => (
                <label
                  key={value}
                  className={`tex-checkbox-item ${selectedStatuses.includes(value) ? 'selected' : ''}`}
                >
                  <input
                    type="checkbox"
                    checked={selectedStatuses.includes(value)}
                    onChange={() => toggleValue(value, selectedStatuses, setSelectedStatuses)}
                  />
                  <span>{getStatusLabel(t, value)}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="tex-field">
            <span className="tex-field-label">
              <FaCalendarAlt className="tex-label-icon" /> {t('ticketExport.dateRange', 'Created between')}
            </span>
            <div className="tex-date-range">
              <input
                type="date"
                value={createdFrom}
                onChange={(event) => setCreatedFrom(event.target.value)}
                aria-label={t('ticketExport.dateFrom', 'From')}
              />
              <span className="tex-date-separator">→</span>
              <input
                type="date"
                value={createdTo}
                onChange={(event) => setCreatedTo(event.target.value)}
                aria-label={t('ticketExport.dateTo', 'To')}
              />
            </div>
          </div>

          <div className="tex-field">
            <span className="tex-field-label">{t('ticketExport.carrierLabel', 'Carriers')}</span>
            <input
              type="text"
              className="tex-text-input"
              value={carrierInput}
              onChange={(event) => setCarrierInput(event.target.value)}
              placeholder={t('ticketExport.carrierPlaceholder', 'e.g. Colissimo, Chronopost (comma separated)')}
            />
          </div>

          <div className="tex-field">
            <span className="tex-field-label">{t('ticketExport.includeLabel', 'Include in the export')}</span>
            <div className="tex-include-list">
              {INCLUDE_OPTIONS.map((value) => (
                <label key={value} className="tex-checkbox-item">
                  <input
                    type="checkbox"
                    checked={includes.includes(value)}
                    onChange={() => toggleValue(value, includes, setIncludes)}
                  />
                  <span>{t(`ticketExport.include_${value}`, value)}</span>
                </label>
              ))}
            </div>
          </div>

          {phase === 'polling' && (
            <div className="tex-status tex-status-info">
              {t('ticketExport.asyncNotice', 'Large export: the file is being generated in the background.')}{' '}
              {job?.status === 'queued'
                ? t('ticketExport.jobQueued', 'Queued…')
                : t('ticketExport.jobRunning', 'Generating file…')}
            </div>
          )}
          {phase === 'done' && (
            <div className="tex-status tex-status-success">
              {t('ticketExport.jobDone', 'Your file is downloading.')}
            </div>
          )}
          {phase === 'error' && (
            <div className="tex-status tex-status-error">{errorMessage}</div>
          )}
        </div>

        <div className="tex-modal-footer">
          <button type="button" className="tex-secondary-button" onClick={onClose} disabled={busy}>
            {t('ticketExport.cancel', 'Cancel')}
          </button>
          <button type="button" className="tex-primary-button" onClick={handleExport} disabled={busy}>
            <FaDownload style={{ marginRight: '8px' }} />
            {busy
              ? t('ticketExport.preparing', 'Preparing…')
              : t('ticketExport.start', 'Export JSON')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TicketExportModal;
